import { Injectable } from "@angular/core";
import { Contract } from "@edp-components/edp-contract";
import {
    ITimeSlot,
    RequestsAndOperations,
    RequestsAndOperationsFactory,
    RequestsAndOperationsFile,
} from "@edp-models/requests-and-operations";
import { EdpResponseBody } from "@edp-models/response.model";
import { Service } from "@edp-models/service.model";
import { ApiService } from "@edp-services/api.service";
import { GetRequestsAndOperations, SetLocalSupportList } from "@edp-store/actions/requests-and-operations.actions";
import { getContract } from "@edp-store/selectors/contract.selectors";
import { localSupportList } from "@edp-store/selectors/requests-and-operations.selector";
import { IAppState } from "@edp-store/state/app.state";
import { select, Store } from "@ngrx/store";
import { BehaviorSubject, combineLatest, Observable } from "rxjs";
import { take } from "rxjs/operators";

export interface INewRequestAndOperation {
    selectedService: Service;
    selectedMotive: Service;
    motiveId?: string;
}

@Injectable({ providedIn: "root" })
export class NewRequestService {
    public request: RequestsAndOperations;
    private subject: BehaviorSubject<INewRequestAndOperation> = new BehaviorSubject<INewRequestAndOperation>(null);
    private attachments: RequestsAndOperationsFile[] = [];
    private canSubmit = false;
    public deleteRequest = {
        motiveName: "Quero que apaguem os meus dados pessoais",
        motive: null, // get id motive by motiveName
        service: "ENERGY",
        prefixLabelMotiveBullet: "pt.edp.edponline.request_operations.delete_request.bullet.",
    };

    constructor(private apiService: ApiService, private store: Store<IAppState>) {}

    public addServiceAndMotive(service: Service, motive: Service) {
        const state = { selectedService: service, selectedMotive: motive };
        if (this.subject) {
            this.subject.next(state);
        } else {
            this.subject = new BehaviorSubject<INewRequestAndOperation>(state);
        }
        this.request.Subject = service.name;
        this.request.ServiceAcronym = service.acronym;
        this.request.Motive = motive.name;
        this.request.ExternalCategory = motive.externalCategory;
        this.request.ID = Number(motive.id);
    }

    public removeServiceAndMotive() {
        if (this.subject) {
            this.subject.next(null);
        }
    }

    public getRequestAndService(): BehaviorSubject<INewRequestAndOperation> {
        return this.subject;
    }

    public selectContract(contract: Contract) {
        this.request = RequestsAndOperationsFactory.create({
            ContractID: contract.ContractId,
        } as RequestsAndOperations);
    }

    public setMessage(message: string) {
        this.request.Description = message;
    }

    public getMessage() {
        return this.request.Description;
    }

    public setCanSubmit(canSubmit: boolean) {
        this.canSubmit = canSubmit;
    }

    public getCanSubmit() {
        return this.canSubmit;
    }

    public uploadAttachment(fileData: File, complaintFile: RequestsAndOperationsFile) {
        const reader: FileReader = new FileReader();

        reader.readAsDataURL(fileData);
        reader.onload = e => {
            complaintFile.base64 = reader.result as string;
            this.apiService.callServicePost("customerSupport.uploadFile", complaintFile).subscribe((response: EdpResponseBody) => {
                if (!response.Success) {
                    complaintFile.error = response.Result.ErrorMessage;
                }
            });
        };
    }

    public addAttachments(files: RequestsAndOperationsFile[]) {
        this.attachments = files;
    }

    public removeAttachment(file: RequestsAndOperationsFile) {
        const index: number = this.attachments.indexOf(file, 0);
        if (index >= 0) {
            this.attachments.splice(index, 1);
        }
    }

    public clearAttachments() {
        this.attachments = [];
    }

    public setCaptchaToken(captchaToken: string) {
        this.request.captchaToken = captchaToken;
    }

    public submit(): Observable<any> {
        this.attachments.forEach(attachment => {
            this.request.Attachments.push({
                DocData: attachment.name,
                MimeType: attachment.mimeType,
                Size: attachment.size,
                Id: attachment.id,
            });
        });

        if (!this.request.ExternalCategory) {
            return this.setData();
        } else {
            return this.apiService.callServicePost("customerSupport.createCustomerSupport", this.request);
        }
    }

    public submitTimeSlots(
        serviceObjectId: string,
        serviceTask: string,
        serviceType: string,
        slotsPeriods: ITimeSlot
    ): Observable<Error | EdpResponseBody> {
        return this.apiService.callServicePost(
            "customerSupport.setTimeSlot",
            {
                serviceObjectId,
                serviceTask,
                serviceType,
                slotsPeriods,
            },
            null,
            false
        );
    }

    public reloadRequests() {
        this.clearAttachments();
        this.store.dispatch(new SetLocalSupportList(true));
        combineLatest([this.store.pipe(select(getContract), take(1)), this.store.pipe(select(localSupportList), take(2))]).subscribe(
            ([contract, localSupportList]) => {
                this.store.dispatch(new GetRequestsAndOperations({contract, localSupportList}));
            }
        );
    }

    public resetData() {
        this.removeServiceAndMotive();
        this.clearAttachments();
        this.request = null;
    }

    public isRequestDelete(): boolean {
        return this.request.ServiceAcronym === this.deleteRequest.service && this.request.Motive === this.deleteRequest.motiveName;
    }

    private setData() {
        const value = this.subject.getValue();
        const payload = {
            ...this.request,
            ...{
                Subject: value.selectedService.name,
                ServiceAcronym: value.selectedService.acronym,
                Motive: value.selectedMotive.name,
                ExternalCategory: value.selectedMotive.externalCategory,
                ID: Number(value.selectedMotive.id),
            },
        } as RequestsAndOperations;

        return this.apiService.callServicePost("customerSupport.createCustomerSupport", payload);
    }
}
